/* eslint-disable */
import React, { useEffect, useState } from 'react';
import Modal from '../../../../shared/modal';
import { useAppDispatch } from '../../../../app/hooks';
import {
    tryToFetchTmReport,
    tryToSaveTmReport,
    tryToExportDocx,
    tryToExportPdf,
} from '../../../../store/admin/journey-reports/journeyReportsSlice';

interface ITmSection {
    cabinId: string;
    aiSummary: string;
    editedSummary: string;
    cabinInput?: string;
    barInput?: string;
    restaurantInput?: string;
}

interface ITmReportModalProps {
    open: boolean;
    tripId: string;
    onClose: () => void;
    onSaved?: () => void;
}

const STATUS_STYLES: Record<string, { bg: string; text: string; dot: string }> = {
    draft: { bg: 'bg-gray-100', text: 'text-gray-700', dot: 'bg-gray-400' },
    in_review: { bg: 'bg-yellow-100', text: 'text-yellow-800', dot: 'bg-yellow-500' },
    signed: { bg: 'bg-green-100', text: 'text-green-800', dot: 'bg-green-500' },
};

const TmReportModal: React.FC<ITmReportModalProps> = ({
    open,
    tripId,
    onClose,
    onSaved,
}) => {
    const dispatch = useAppDispatch();

    const [viewMode, setViewMode] = useState<'sections' | 'full'>('sections');
    const [sections, setSections] = useState<ITmSection[]>([]);
    const [editedDraft, setEditedDraft] = useState('');
    const [status, setStatus] = useState('draft');
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isExporting, setIsExporting] = useState<'docx' | 'pdf' | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [expandedHod, setExpandedHod] = useState<Record<number, boolean>>({});

    /* ── Load report on open ── */
    useEffect(() => {
        if (!open || !tripId) return;
        setIsLoading(true);
        setError(null);
        dispatch(tryToFetchTmReport(tripId))
            .unwrap()
            .then((data: any) => {
                const secs: ITmSection[] = (data?.sections || []).map((s: any) => ({
                    cabinId: s.cabinId || '',
                    aiSummary: s.aiSummary || '',
                    editedSummary: s.editedSummary ?? s.aiSummary ?? '',
                    cabinInput: s.cabinInput || '',
                    barInput: s.barInput || '',
                    restaurantInput: s.restaurantInput || '',
                }));
                setSections(secs);
                setEditedDraft(data?.editedDraft || data?.draft || '');
                setStatus(data?.status || 'draft');
            })
            .catch((err: any) => {
                setError(typeof err === 'string' ? err : 'Failed to load TM report');
            })
            .finally(() => setIsLoading(false));
    }, [open, tripId]);

    /* ── Reset on close ── */
    useEffect(() => {
        if (!open) {
            setSections([]);
            setEditedDraft('');
            setStatus('draft');
            setError(null);
            setViewMode('sections');
            setExpandedHod({});
        }
    }, [open]);

    /* ── Handlers ── */
    const handleSectionChange = (idx: number, value: string) => {
        setSections((prev) =>
            prev.map((s, i) => (i === idx ? { ...s, editedSummary: value } : s)),
        );
    };

    const toggleHod = (idx: number) => {
        setExpandedHod((prev) => ({ ...prev, [idx]: !prev[idx] }));
    };

    const handleSave = async () => {
        if (isSaving) return;
        setIsSaving(true);
        setError(null);
        try {
            const payload: any = {};
            if (viewMode === 'sections') {
                payload.sections = sections.map((s) => ({
                    cabinId: s.cabinId,
                    editedSummary: s.editedSummary,
                }));
            } else {
                payload.editedDraft = editedDraft;
            }
            await dispatch(tryToSaveTmReport({ tripId, data: payload })).unwrap();
            onSaved?.();
            onClose();
        } catch (err: any) {
            setError(typeof err === 'string' ? err : 'Failed to save TM report');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDownload = async (format: 'docx' | 'pdf') => {
        if (isExporting) return;
        setIsExporting(format);
        try {
            const thunk = format === 'docx' ? tryToExportDocx : tryToExportPdf;
            const data = await dispatch(thunk(tripId)).unwrap();
            const url = data?.url || data?.pdfUrl || data?.docxUrl;
            if (!url) {
                setError(`No ${format.toUpperCase()} URL returned`);
                return;
            }
            try {
                const response = await fetch(url);
                const blob = await response.blob();
                const blobUrl = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = `tm-report-${tripId}.${format}`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
            } catch {
                window.open(url, '_blank');
            }
        } catch (err: any) {
            setError(typeof err === 'string' ? err : `Failed to export ${format.toUpperCase()}`);
        } finally {
            setIsExporting(null);
        }
    };

    const statusStyle = STATUS_STYLES[status] || STATUS_STYLES.draft;

    return (
        <Modal
            open={open}
            onClose={onClose}
            contentContainerStyle='max-w-[95%] w-[800px] rounded-lg mb-4'
            title='Train Manager Report'
        >
            <div className='font-inter'>
                {/* Status badge */}
                <div className='mb-4 flex items-center gap-3'>
                    <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}
                    >
                        <span className={`w-1.5 h-1.5 rounded-full ${statusStyle.dot}`} />
                        {status.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                    </span>
                </div>

                {/* View mode toggle */}
                {!isLoading && sections.length > 0 && (
                    <div className='mb-4 flex gap-1 p-1 bg-gray-100 rounded-lg w-fit'>
                        <button
                            onClick={() => setViewMode('sections')}
                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${viewMode === 'sections' ? 'bg-white shadow-sm text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            By Cabin
                        </button>
                        <button
                            onClick={() => setViewMode('full')}
                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${viewMode === 'full' ? 'bg-white shadow-sm text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            Full Report
                        </button>
                    </div>
                )}

                {/* Loading */}
                {isLoading && (
                    <div className='flex items-center justify-center py-12'>
                        <div className='w-6 h-6 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin' />
                    </div>
                )}

                {/* Error */}
                {error && (
                    <div className='mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm'>
                        {error}
                    </div>
                )}

                {/* Section view */}
                {!isLoading && viewMode === 'sections' && sections.length > 0 && (
                    <div className='space-y-5 max-h-[55vh] overflow-y-auto pr-1'>
                        {sections.map((section, idx) => (
                            <div
                                key={section.cabinId + idx}
                                className='border border-[#E9EAEB] rounded-lg p-4'
                            >
                                <h4 className='text-sm font-semibold text-gray-800 mb-2'>
                                    {section.cabinId || `Section ${idx + 1}`}
                                </h4>

                                <textarea
                                    rows={5}
                                    value={section.editedSummary}
                                    onChange={(e) => handleSectionChange(idx, e.target.value)}
                                    disabled={isSaving}
                                    className='w-full border border-[#E9EAEB] rounded-lg p-3 text-sm text-gray-800 resize-y focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:bg-gray-50'
                                />

                                {/* Collapsible HOD inputs */}
                                {(section.cabinInput || section.barInput || section.restaurantInput) && (
                                    <div className='mt-3'>
                                        <button
                                            onClick={() => toggleHod(idx)}
                                            className='text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1'
                                        >
                                            <svg
                                                className={`w-3 h-3 transition-transform ${expandedHod[idx] ? 'rotate-90' : ''}`}
                                                fill='none'
                                                viewBox='0 0 24 24'
                                                stroke='currentColor'
                                            >
                                                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7' />
                                            </svg>
                                            HOD Inputs
                                        </button>
                                        {expandedHod[idx] && (
                                            <div className='mt-2 space-y-2 pl-3 border-l-2 border-blue-100'>
                                                {section.cabinInput && (
                                                    <div>
                                                        <p className='text-[11px] font-semibold text-gray-500 uppercase tracking-wide'>Cabin</p>
                                                        <p className='text-xs text-gray-700 mt-0.5 whitespace-pre-wrap'>{section.cabinInput}</p>
                                                    </div>
                                                )}
                                                {section.barInput && (
                                                    <div>
                                                        <p className='text-[11px] font-semibold text-gray-500 uppercase tracking-wide'>Bar</p>
                                                        <p className='text-xs text-gray-700 mt-0.5 whitespace-pre-wrap'>{section.barInput}</p>
                                                    </div>
                                                )}
                                                {section.restaurantInput && (
                                                    <div>
                                                        <p className='text-[11px] font-semibold text-gray-500 uppercase tracking-wide'>Restaurant</p>
                                                        <p className='text-xs text-gray-700 mt-0.5 whitespace-pre-wrap'>{section.restaurantInput}</p>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Full draft view */}
                {!isLoading && viewMode === 'full' && (
                    <div className='max-h-[55vh] overflow-y-auto pr-1'>
                        <textarea
                            rows={18}
                            value={editedDraft}
                            onChange={(e) => setEditedDraft(e.target.value)}
                            disabled={isSaving}
                            className='w-full border border-[#E9EAEB] rounded-lg p-3 text-sm text-gray-800 resize-y focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:bg-gray-50'
                        />
                    </div>
                )}

                {/* Empty state */}
                {!isLoading && !error && sections.length === 0 && !editedDraft && (
                    <div className='text-center py-12 text-gray-400 text-sm'>
                        No report data available yet.
                    </div>
                )}

                {/* Footer actions */}
                {!isLoading && (sections.length > 0 || editedDraft) && (
                    <div className='flex flex-wrap items-center justify-between gap-3 mt-6 pt-4 border-t border-[#E9EAEB]'>
                        <div className='flex gap-2'>
                            <button
                                onClick={() => handleDownload('docx')}
                                disabled={!!isExporting}
                                className='px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50 flex items-center gap-1.5'
                            >
                                {isExporting === 'docx' ? (
                                    <div className='w-3 h-3 border-2 border-gray-400 border-t-gray-700 rounded-full animate-spin' />
                                ) : (
                                    <img src='/svg/file-docx-color-green-icon.svg' alt='DOCX' className='w-4 h-4' />
                                )}
                                DOCX
                            </button>
                            <button
                                onClick={() => handleDownload('pdf')}
                                disabled={!!isExporting}
                                className='px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50 flex items-center gap-1.5'
                            >
                                {isExporting === 'pdf' ? (
                                    <div className='w-3 h-3 border-2 border-gray-400 border-t-gray-700 rounded-full animate-spin' />
                                ) : (
                                    <img src='/svg/download-pdf-icon.svg' alt='PDF' className='w-4 h-4 dark:contrast-0' />
                                )}
                                PDF
                            </button>
                        </div>
                        <div className='flex gap-3'>
                            <button
                                onClick={onClose}
                                className='px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition'
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={isSaving}
                                className='px-5 py-2 text-sm font-medium text-white bg-[#2D8EFF] rounded-lg hover:bg-[#2578DB] transition disabled:opacity-50 flex items-center gap-2'
                            >
                                {isSaving && (
                                    <div className='w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin' />
                                )}
                                Save
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default TmReportModal;
